<template>
  <div :class="wrapperClassObj">
    <div :class="$style.inputWrapper">
      <UiKitTypography :class="placeholderClassObj" :type="MAP.CAPTION02D">{{
        label
      }}</UiKitTypography>
      <input
        ref="inputElement"
        v-model="model"
        :placeholder="props.placeholder"
        @focus="focused = true"
        @blur="focused = false"
        :type="showPassword ? 'text' : props.type"
        :disabled="props.disabled"
        :readOnly="props.readOnly"
        :autocomplete="props.autocomplete"
        :maxlength="props.maxLength"
        v-bind="$attrs"
      />
      <div v-if="props.passwordEye" :class="$style.eyeWrapper">
        <img
          v-if="showPassword"
          src="@/images/icons/eye-opened.svg"
          alt="eye"
          @click="showPassword = !showPassword"
        />
        <img
          v-else
          src="@/images/icons/eye-closed.svg"
          alt="eye"
          @click="showPassword = !showPassword"
        />
      </div>
    </div>
    <div :class="$style.rightButton">
      <slot name="right-button" :value="model"></slot>
    </div>
    <UiKitTypography
      v-if="error"
      :class="$style.errorMessage"
      :type="MAP.CAPTION02D"
    >
      {{ error }}
    </UiKitTypography>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  useCssModule,
  defineProps,
  defineModel,
  defineOptions,
  defineExpose,
} from "vue";
import { MAP } from "@/constants/typography";
import UiKitTypography from "@/ui-kit/Typography.vue";

defineOptions({
  inheritAttrs: false,
});

const model = defineModel();
const focused = ref(false);
const inputElement = ref(null);
const showPassword = ref(false);
const props = defineProps({
  class: String,
  label: String,
  placeholder: String,
  variant: String,
  noLabel: Boolean,
  error: String,
  type: String,
  autocomplete: [Boolean, String],
  readOnly: Boolean,
  disabled: Boolean,
  passwordEye: Boolean,
  maxLength: [Number, String],
  // Any other props are collected in $attrs and passed to the input element
});
const $style = useCssModule();

const wrapperClassObj = computed(() => ({
  [$style.wrapper]: true,
  [$style.white]: props.variant === "white",
  [$style.noLabel]: props.noLabel,
  [$style.error]: props.error,
  [$style.disabled]: props.disabled || props.readOnly,
  [$style.passwordEye]: props.passwordEye,
  [props.class]: props.class,
}));

const placeholderClassObj = computed(() => ({
  [$style.placeholder]: true,
  [$style.minified]: true, // focused.value || model.value?.length,
}));

// Expose a focus method that the parent can call
defineExpose({
  focus() {
    inputElement.value?.focus();
  },
});
</script>

<style module lang="scss">
.wrapper {
  position: relative;
  display: flex;
  align-items: center;
  position: relative;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  border: 1px solid #d8d8d8;
  height: 56px;

  .rightButton {
    padding: 0 16px;
    border-left: 1px solid #d8d8d8;

    &:empty {
      display: none;
    }
  }

  &:focus,
  &:hover {
    border: 1px solid #050505;
    outline: none;

    .rightButton {
      border-left: 1px solid #050505;
    }
  }

  .inputWrapper {
    position: relative;
    height: 100%;
    flex-grow: 1;

    .eyeWrapper {
      position: absolute;
      height: 16px;
      right: 16px;
      top: 50%;
      transform: translateY(-50%);
      cursor: pointer;
    }

    .placeholder {
      position: absolute;
      color: #6b6b6b;
      pointer-events: none;
      font-size: 16px;
      font-weight: 400;
      top: 11px;
      left: 16px;
      transition: font-size 0.2s, top 0.2s;

      &.minified {
        font-size: 12px;
        top: 8px;
      }
    }

    input {
      padding: 20px 16px 0px;
      width: 100%;
      height: 100%;
      background: transparent;
      border: none;

      color: #050505;
      font-family: "Noto Sans";
      font-size: 14px;
      font-weight: 400;
      line-height: 150%;
      border-radius: 8px;

      &::placeholder {
        color: #6b6b6b;
        opacity: 0.4;
      }
    }
  }
}

.white {
  border: 1px solid #d8d8d8;
  background: none;

  .rightButton {
    border-left: 1px solid #d8d8d8;
  }

  .inputWrapper {
    input {
      color: #fff;

      &::placeholder {
        color: #6b6b6b;
      }
    }
  }

  &:focus,
  &:hover {
    border: 1px solid #050505;
    outline: none;

    .rightButton {
      border-left: 1px solid #050505;
    }
  }
}

.noLabel {
  height: 56px;
  padding: 0;
  .inputWrapper {
    height: 100%;
    input {
      height: 100%;
      padding: 0 16px;
      border-radius: 8px;
    }
  }
}

.disabled {
  background: rgba(5, 5, 5, 0.03);
  border: 1px solid rgba(5, 5, 5, 0.03);

  &:hover {
    border: 1px solid rgba(5, 5, 5, 0.03);
  }
}

.error {
  border: 1px solid #d83f48;

  .errorMessage {
    position: absolute;
    color: #d83f48;
    bottom: -20px;
    padding-left: 16px;
    left: 0;

    &:first-letter {
      text-transform: uppercase;
    }
  }

  .rightButton {
    border-left: 1px solid #d83f48;
  }

  &:focus,
  &:hover {
    border: 1px solid #d83f48;
    outline: none;

    .rightButton {
      border-left: 1px solid #d83f48;
    }
  }

  .inputWrapper {
    .placeholder {
      color: #d83f48;
    }
  }
}

.passwordEye {
  .inputWrapper {
    input {
      padding-right: 40px;
    }
  }
}
</style>
